import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { DownloadSimple, Printer, Broom, Warning } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtDate } from "@/lib/api";
import { CopyButton } from "@/components/common";
import { PORTRAIT, drawHkCard } from "@/lib/portrait";
import { save } from "./PrintKit";

const DEFAULTS = ["Change bed sheets & pillowcases", "Fresh towels in bathroom", "Clean & disinfect bathroom", "Clean kitchen & wash dishes", "Empty all bins", "Sweep & mop floors", "Restock toilet paper, soap & water", "Check lights, TV remote & Wi-Fi", "Check for damage or missing items", "Lock windows & doors"];

function printPortrait(c) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><body style="margin:0;display:grid;place-items:center;min-height:100vh"><img src="${c.toDataURL("image/png")}" style="width:54mm" onload="setTimeout(()=>{window.print()},200)"/></body></html>`);
  w.document.close();
}

function ReportPhoto({ rid, i }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/hk-reports/${rid}/photos/${i}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [rid, i]);
  if (!url) return <div className="h-20 w-20 animate-pulse rounded-lg bg-secondary" />;
  return <a href={url} target="_blank" rel="noreferrer" data-testid={`hk-report-photo-${rid}-${i}`}><img src={url} alt="Clean" className="h-20 w-20 rounded-lg object-cover transition-transform duration-200 hover:scale-105" /></a>;
}

export function Housekeeping({ property, d, up }) {
  const url = `${window.location.origin}/h/${property.hk_code}`;
  const canvas = useRef();
  const qr = useRef();
  const [reports, setReports] = useState([]);
  const items = d.housekeeping ?? DEFAULTS;

  useEffect(() => { api.get(`/properties/${property.property_id}/housekeeping`).then((r) => setReports(r.data)); }, [property.property_id]);
  useEffect(() => {
    document.fonts.ready.then(() => canvas.current && drawHkCard(canvas.current, { name: d.name, qr: qr.current?.querySelector("canvas") }));
  }, [d.name, url]);

  return (
    <div className="space-y-5" data-testid="housekeeping-tab">
      <section className="rounded-2xl border border-border bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-lg"><Broom /> Housekeeper checklist</h3>
        <p className="mt-1 text-sm text-muted-foreground">One task per line. Housekeepers scan the card, tick what they did and leave a comment. You get an email each time.</p>
        <Textarea rows={8} className="mt-4" value={items.join("\n")} onChange={(e) => up((x) => { x.housekeeping = e.target.value.split("\n"); })}
          onBlur={() => up((x) => { x.housekeeping = (x.housekeeping ?? DEFAULTS).map((i) => i.trim()).filter(Boolean); })} data-testid="housekeeping-items-input" />
        <p className="mt-2 text-xs text-muted-foreground">Remember to Save after editing the list.</p>
      </section>
      <section className="grid gap-6 rounded-2xl border border-border bg-white p-6 sm:grid-cols-[auto,1fr]">
        <div className="hidden" ref={qr}><QRCodeCanvas value={url} size={400} marginSize={1} /></div>
        <canvas ref={canvas} width={PORTRAIT[0]} height={PORTRAIT[1]} className="w-[200px] rounded-[14px] border border-border shadow-md" data-testid="housekeeping-card-canvas" />
        <div className="min-w-0">
          <h3 className="font-display text-lg">Housekeeping card</h3>
          <p className="mt-1 text-sm text-muted-foreground">Stick it inside a cupboard or the cleaning store, away from guests.</p>
          <div className="mt-3 rounded-lg bg-secondary px-3 py-2.5 font-mono text-xs break-all" data-testid="housekeeping-url">{url}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <CopyButton value={url} label="Copy link" done="Link copied" testId="housekeeping-copy-link" className="h-9 rounded-lg bg-[#151312] px-3 text-sm font-semibold text-white" />
            <button onClick={() => save(canvas.current, `${d.name}-housekeeping.png`)} data-testid="housekeeping-download-card" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold"><DownloadSimple /> PNG</button>
            <button onClick={() => printPortrait(canvas.current)} data-testid="housekeeping-print-card" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-semibold"><Printer /> Print</button>
            <a href={url} target="_blank" rel="noreferrer" data-testid="housekeeping-open-page" className="inline-flex h-9 items-center rounded-lg border border-border px-3 text-sm font-semibold">Open page</a>
          </div>
        </div>
      </section>
      <section className="rounded-2xl border border-border bg-white p-6" data-testid="housekeeping-reports">
        <h3 className="font-display text-lg">Recent cleans</h3>
        {reports.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No reports yet.</p>}
        <div className="mt-3 divide-y divide-border">
          {reports.map((r) => (
            <div key={r.report_id} className="py-3 text-sm" data-testid={`hk-report-${r.report_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">{r.name}{r.issues && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F9DED4] px-2 py-0.5 text-xs text-[#A6431F]"><Warning size={12} /> Issue</span>}</span>
                <span className="text-muted-foreground">{r.checked.length}/{r.checked.length + r.missed.length} done · {fmtDate(r.created_at)}</span>
              </div>
              {r.missed.length > 0 && <p className="mt-1 text-xs text-[#A6431F]">Not ticked: {r.missed.join(", ")}</p>}
              {r.comments && <p className="mt-1 text-muted-foreground">“{r.comments}”</p>}
              {r.photos?.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{r.photos.map((_, i) => <ReportPhoto key={i} rid={r.report_id} i={i} />)}</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
